import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Target, Lightbulb, Heart, Globe, Shield, Zap, CheckCircle2, Dumbbell, Brain, Pill, Activity } from "lucide-react";

const FadeUp = ({ children, delay = 0, className = "" }: {
  children: React.ReactNode; delay?: number; className?: string;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const VALUES = [
  { icon: Target,    title: "Proactive, not reactive",  color: "text-ay-cyan",
    desc: "We believe the best healthcare is the kind you never need. AlphaYou helps you stay well — not just recover from illness." },
  { icon: Dumbbell,  title: "Precision over generic",   color: "text-ay-emerald",
    desc: "You wouldn't follow a generic gym programme. Why take generic supplements? Your body is unique. Your nutrition plan should be too." },
  { icon: Brain,     title: "AI with clinical rigour",  color: "text-ay-amber",
    desc: "We're a technology company first. Our AI is grounded in NHS, SACN, NICE, and BNF — not marketing claims or US standards." },
  { icon: Heart,     title: "People-centred",           color: "text-rose-400",
    desc: "Built with input from GPs, pharmacists, nutritionists, and real users. Every feature solves a real health problem." },
  { icon: Globe,     title: "UK-first, always",         color: "text-violet-400",
    desc: "UK latitude, UK demographics, UK food culture, UK clinical guidelines. Not US RDAs retrofitted for Britain." },
  { icon: Shield,    title: "Safety by design",         color: "text-sky-400",
    desc: "Every recommendation includes clinical disclaimers and NHS references. AI augments your health decisions — it doesn't replace your GP." },
];

const TIMELINE = [
  { year: "2025 Q4", title: "The insight",          desc: "Identified the gap: millions of UK people taking the wrong supplements because no platform accounts for UK-specific biology, diet, and guidelines." },
  { year: "2026 Q1", title: "v0.1 — Core engine",   desc: "Launched the vitamin intelligence engine. 15+ UK clinical rules. Sub-second personalised recommendations. 23/23 tests passing." },
  { year: "2026 Q2", title: "v0.2 — AI depth",      desc: "Added live NICE/BNF retrieval, lab result interpretation, microbiome reports. AlphaYou becomes a full health AI platform." },
  { year: "2026 Q3", title: "v0.3 — ML model",      desc: "XGBoost personalisation model trained on NDNS data. SHAP explainability. Usage analytics. Mobile-optimised experience." },
  { year: "2026 Q4", title: "v1.0 — Enterprise",    desc: "NHS trust integration, SAML SSO, clinical trial support, multi-tenant architecture. The full health AI stack." },
];

export default function AboutPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative section-pad pt-32 pb-20">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="orb orb-cyan w-[500px] h-[500px] -top-40 -left-40 opacity-15" />
        <div className="container-ay relative z-10">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="badge-violet mb-6">Our story</div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
                We're a health AI company.<br />
                <span className="text-gradient-cyan">Vitamins are just the start.</span>
              </h1>
              <p className="text-slate-400 text-xl leading-relaxed mb-5">
                AlphaYou was born from a simple observation: people invest heavily in their fitness —
                gym memberships, personal trainers, running shoes — but completely neglect the nutritional
                foundation that makes all of that effort actually work.
              </p>
              <p className="text-slate-400 text-xl leading-relaxed">
                We built an AI platform that tells you exactly what your body needs, grounded in UK
                clinical science — not US marketing. Proactive health, not reactive medicine.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The vision */}
      <section className="section-pad" style={{ background: "rgba(13,18,32,0.6)" }}>
        <div className="container-ay">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <div className="badge-cyan mb-4">The vision</div>
              <h2 className="text-4xl font-black text-white mb-6">
                Health intelligence that's <span className="text-gradient-cyan">truly yours</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-5">
                Think about why you go to the gym. Not because you're ill — because you want to be
                stronger, more energetic, more resilient. That's proactive health.
              </p>
              <p className="text-slate-400 leading-relaxed mb-5">
                But exercise alone isn't enough. Your body needs the right micronutrients to perform,
                recover, and thrive. The problem is that nutritional needs are deeply personal —
                shaped by your age, sex, ethnicity, UK region, diet, season, and health history.
              </p>
              <p className="text-slate-400 leading-relaxed mb-8">
                AlphaYou uses AI to solve this precisely. Not generic advice. Not US RDAs.
                <strong className="text-white"> Personalised recommendations grounded in NHS, SACN, and NICE guidelines</strong> —
                built specifically for UK bodies.
              </p>
              <div className="space-y-3">
                {[
                  "Personalised supplement plans in under 2 minutes",
                  "Grounded in NHS, SACN, NICE, and BNF — not US standards",
                  "Accounts for UK latitude, ethnicity, season, and diet",
                  "Drug-nutrient interactions from the British National Formulary",
                  "Proactive health intelligence — not reactive medicine",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <CheckCircle2 size={15} className="text-ay-cyan flex-shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="space-y-4">
                <div className="ay-card border border-ay-cyan/20 p-6">
                  <div className="text-5xl font-black text-gradient-cyan mb-2">£1.2bn</div>
                  <div className="text-white font-semibold mb-1">Spent on supplements in the UK annually</div>
                  <div className="text-ay-muted text-sm">Most without any personalised guidance. AlphaYou changes that.</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Pill,     label: "Vitamin intelligence",  color: "text-ay-cyan",    bg: "bg-ay-cyan/10",    border: "border-ay-cyan/20" },
                    { icon: Activity, label: "Lab interpretation",    color: "text-ay-emerald", bg: "bg-ay-emerald/10", border: "border-ay-emerald/20" },
                    { icon: Brain,    label: "Clinical AI Q&A",       color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
                    { icon: Zap,      label: "Health reports",        color: "text-ay-amber",   bg: "bg-ay-amber/10",   border: "border-ay-amber/20" },
                  ].map(({ icon: Icon, label, color, bg, border }) => (
                    <div key={label} className={`ay-card border ${border} p-4 flex items-center gap-3`}>
                      <div className={`w-9 h-9 rounded-lg ${bg} border ${border} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={16} className={color} />
                      </div>
                      <span className="text-sm text-slate-300 font-medium">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad">
        <div className="container-ay">
          <FadeUp className="text-center mb-14">
            <div className="badge-emerald mb-4">What we believe</div>
            <h2 className="text-4xl font-black text-white mb-4">Our values</h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map(({ icon: Icon, title, desc, color }, i) => (
              <FadeUp key={title} delay={i * 0.07}>
                <div className="ay-card h-full">
                  <Icon size={22} className={`${color} mb-4`} />
                  <h3 className="font-bold text-white mb-2">{title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-pad" style={{ background: "rgba(13,18,32,0.5)" }}>
        <div className="container-ay max-w-3xl">
          <FadeUp className="text-center mb-14">
            <div className="badge-violet mb-4">Roadmap</div>
            <h2 className="text-4xl font-black text-white mb-4">Where we're going</h2>
          </FadeUp>
          <div className="relative">
            <div className="absolute left-[88px] top-0 bottom-0 w-px bg-ay-border" />
            <div className="space-y-8">
              {TIMELINE.map(({ year, title, desc }, i) => (
                <FadeUp key={year} delay={i * 0.08}>
                  <div className="flex gap-6 items-start">
                    <div className="w-[88px] flex-shrink-0 text-right">
                      <span className="text-xs font-mono text-ay-cyan">{year}</span>
                    </div>
                    <div className="relative">
                      <div className="absolute -left-[25px] top-1 w-3 h-3 rounded-full border-2 border-ay-cyan bg-ay-bg" />
                      <div className="ay-card ml-4">
                        <h4 className="font-bold text-white mb-1">{title}</h4>
                        <p className="text-sm text-slate-400">{desc}</p>
                      </div>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container-ay text-center">
          <FadeUp>
            <h2 className="text-4xl font-black text-white mb-4">
              Join us in building the future of proactive health
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Whether you're an individual looking to optimise your health, or a life sciences company
              building on our platform — we'd love to hear from you.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/app" className="btn-primary text-base px-8 py-3.5">
                Try AlphaYou free <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-outline text-base px-8 py-3.5">
                Get in touch
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
